import { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { isNil } from 'lodash';

import type { AnalyticsRecord } from '@/utils/services/analytics/AnalyticsDataService';

import { useClickhouseMediaAnalytics } from '@/utils/hooks/featureFlag/useClickhouseMediaAnalytics';
import useAnalytics from '@/utils/hooks/swr/useAnalytics';
import useMediaAnalytics from '@/utils/hooks/swr/useMediaAnalytics';
import useSocialProfilePermissions from '@/utils/hooks/swr/useSocialProfilePermissions';
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import type { SocialProfileNetworkType } from '@/types/socialProfileType';
import type {
    KpiSplit,
    Post,
    StatsConfig
} from '@/components/composite/MediaViewer/components/MediaViewerStats/types';

import { MediaViewerStatsConfig } from '@/lib/analytics/mediaViewer';
import { SIZE_FULL } from '@/lib/gridElements/size';
import { CUSTOM } from '@/lib/periods';
import { POST, STORY, TW_POST_TYPES } from '@/lib/posts/postTypes';
import { FACEBOOK, X } from '@/lib/socialProfile';
import { EXCLUDED_NO_FOLLOWERS_KPIS } from '@/components/composite/MediaViewer/components/MediaViewerStats/const';

import InfoBanner from '@/components/base/InfoBanner';
import AnalyticsGrid, { GRID_STYLES } from '@/components/layouts/wrappers/AnalyticsGrid';
import { Trans } from '@/components/tools/translation';
import MediaViewerEstimatedStatsBanner from '@/components/composite/MediaViewer/components/MediaViewerEstimatedStatsBanner';
import MediaViewerIncompleteStatsBanner from '@/components/composite/MediaViewer/components/MediaViewerIncompleteStatsBanner';
import { ChartGridItem } from '@/components/composite/MediaViewer/components/MediaViewerStats/ChartGridItem';
import { KpiGridItem } from '@/components/composite/MediaViewer/components/MediaViewerStats/KpiGridItem';

const shouldRenderKpi = (
    config: StatsConfig,
    socialNetworkType: SocialProfileNetworkType,
    postFollowersValue: number | null
): boolean => {
    const excludedKpis =
        EXCLUDED_NO_FOLLOWERS_KPIS?.[
            socialNetworkType as keyof typeof EXCLUDED_NO_FOLLOWERS_KPIS
        ] ?? [];
    const isExcluded = excludedKpis.includes(config.id as never);
    return !isExcluded || postFollowersValue !== null;
};

type MediaViewerStatsProps = {
    post?: Post | null;
    socialNetworkType: SocialProfileNetworkType;
    socialNetworkId: number;
    postFormat?: typeof POST | typeof STORY;
    isLoading?: boolean;
};

type AnalyticsParams = {
    period: string;
    start: string | null;
    end: string | null;
    fields: string[];
    parameters: Record<string, { id_media?: string | number; type?: string }>;
    disable_previous: boolean;
} | null;

const MediaViewerStats = ({
    post,
    socialNetworkType,
    socialNetworkId,
    postFormat = POST,
    isLoading = true
}: MediaViewerStatsProps) => {
    const { t } = useTranslation();
    const { user, isLoading: isUserLoading } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const [paramsAnalyticsRequest, setParamsAnalyticsRequest] = useState<AnalyticsParams>(null);

    // Check if clickhouse media analytics is active for the current network
    const { isActiveForNetwork } = useClickhouseMediaAnalytics();
    const shouldUseAnalyticsV2 = isActiveForNetwork(socialNetworkType);
    const statsFormatConfig = MediaViewerStatsConfig(socialNetworkType)?.[postFormat];

    const getStatsConfigKey = (): string | undefined => {
        if (isLoading && statsFormatConfig) {
            const keys = Object.keys(statsFormatConfig);
            return keys.length > 0 ? String(keys[0]) : undefined;
        }
        return post?.type;
    };

    const statsConfig: StatsConfig[] | null = statsFormatConfig
        ? statsFormatConfig[getStatsConfigKey() ?? '']
        : null;

    // Get required fields for KPIs from config
    const requiredFields =
        statsConfig?.filter((config) => config.type === 'kpi')?.map((config) => config.id) ?? [];

    // Get chart fields and parameters for legacy analytics
    const getChartAnalyticsParams = (): AnalyticsParams => {
        const fields: string[] = [];
        const parameters: Record<string, { id_media?: string | number; type?: string }> = {};
        const date =
            post?.created || post?.published_at
                ? dayjs(post?.created ?? post?.published_at).format('YYYY-MM-DD')
                : null;

        statsConfig
            ?.filter((config) => config.type === 'chart')
            ?.forEach((config) => {
                fields.push(config.id);
                parameters[config.id] = { id_media: post?.id };
                if (config.defaultFilter) {
                    parameters[config.id].type = config.defaultFilter;
                }
            });

        return {
            period: CUSTOM,
            start: date,
            end: date,
            fields,
            parameters,
            disable_previous: true
        };
    };

    // For V2: use media-specific endpoint for KPIs (returns flat data)
    const {
        analytics: mediaAnalytics,
        analyticsError: mediaAnalyticsError,
        mutateAnalytics: mutateMediaAnalytics,
        isLoading: isLoadingMediaAnalytics
    } = useMediaAnalytics({
        mediaId: post?.id,
        socialProfileId: socialNetworkId,
        requiredFields,
        shouldFetch: shouldUseAnalyticsV2 && !!post?.id
    });

    // For legacy: use general analytics endpoint for charts
    const {
        analytics: legacyAnalytics,
        analyticsError: legacyAnalyticsError,
        mutateAnalytics: mutateLegacyAnalytics,
        isLoading: isLoadingLegacyAnalytics
    } = useAnalytics(socialNetworkId, paramsAnalyticsRequest, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const { permissions, isLoading: isLoadingPermission } =
        useSocialProfilePermissions(socialNetworkId);

    // Set legacy params for charts only when not using V2
    useEffect(() => {
        if (!shouldUseAnalyticsV2 && post?.id && (post?.created || post?.published_at)) {
            setParamsAnalyticsRequest(getChartAnalyticsParams());
        }
    }, [
        post?.created,
        post?.published_at,
        post?.id,
        post?.type,
        shouldUseAnalyticsV2,
        statsConfig
    ]);

    // Unified analytics state based on which hook is active
    const rawAnalytics: AnalyticsRecord | null = shouldUseAnalyticsV2
        ? mediaAnalytics
        : legacyAnalytics;

    // Transform analytics to include video_views_quartile chart data
    // This allows ChartGridItem to find the data under analytics.video_views_quartile
    const analytics: AnalyticsRecord | null = rawAnalytics
        ? {
              ...rawAnalytics,
              video_views_quartile: {
                  graphData: [
                      {
                          label: '0%',
                          value: (rawAnalytics.playback_0_count as unknown as number) ?? 0
                      },
                      {
                          label: '25%',
                          value: (rawAnalytics.playback_25_count as unknown as number) ?? 0
                      },
                      {
                          label: '50%',
                          value: (rawAnalytics.playback_50_count as unknown as number) ?? 0
                      },
                      {
                          label: '75%',
                          value: (rawAnalytics.playback_75_count as unknown as number) ?? 0
                      },
                      {
                          label: '100%',
                          value: (rawAnalytics.playback_100_count as unknown as number) ?? 0
                      }
                  ]
              }
          }
        : null;
    const analyticsError = shouldUseAnalyticsV2 ? mediaAnalyticsError : legacyAnalyticsError;
    const mutateAnalytics = shouldUseAnalyticsV2 ? mutateMediaAnalytics : mutateLegacyAnalytics;
    const isLoadingAnalytics = shouldUseAnalyticsV2
        ? isLoadingMediaAnalytics
        : isLoadingLegacyAnalytics;

    const getKpiSplit = (idKpi: string): KpiSplit => {
        // For V2, get split data from analytics response; for legacy, from post object
        const dataSource = shouldUseAnalyticsV2 ? analytics : post;
        const organic = (dataSource?.[`${idKpi}_organic`] as number | null) ?? null;
        const paid = (dataSource?.[`${idKpi}_paid`] as number | null) ?? null;
        if (organic !== null || paid !== null) {
            if (socialNetworkType === FACEBOOK) {
                return { organic_viral: organic, paid };
            }
            return { organic, paid };
        }
        return null;
    };

    const postFollowers = {
        value: post?.subscribers ?? post?.followers ?? null,
        estimated: post?.subscribers_estimated ?? post?.followers_estimated ?? false
    };

    const getKpiValue = (configId: string): unknown => {
        return shouldUseAnalyticsV2 ? analytics?.[configId] : post?.[configId];
    };

    const handleRetry = () => {
        mutateAnalytics();
    };

    // Check if engagement_rate is available in the analytics response
    const isEngagementRateUnavailable =
        isNil(analytics?.engagement_rate) && isNil(post?.engagement_rate);

    // Check if this is a repost (retweet) on X platform
    const isRepost = socialNetworkType === X && post?.type === TW_POST_TYPES.TYPE_RETWEET;

    return (
        <AnalyticsGrid gridStyle={GRID_STYLES.MEDIA_VIEWER}>
            {/* Info banner for reposts on X platform */}
            {isRepost && !isLoadingAnalytics ? (
                <AnalyticsGrid.Item size={SIZE_FULL} className="!min-h-0">
                    <InfoBanner
                        message={t('analytics:twitter_repost_info_media_viewer')}
                        type="info"
                    />
                </AnalyticsGrid.Item>
            ) : null}
            {/* Warning banner if engagement_rate is not available in analytics */}
            {post && isEngagementRateUnavailable && !isLoadingAnalytics ? (
                <AnalyticsGrid.Item
                    size={SIZE_FULL}
                    className="!min-h-0 rounded-m bg-icoGray-300 p-6 text-center font-GMedium text-xs text-icoGray-500 dark:bg-icoDarkMode-anthracite dark:text-icoDarkMode-beton">
                    <MediaViewerIncompleteStatsBanner
                        type={postFormat}
                        network={socialNetworkType}
                    />
                </AnalyticsGrid.Item>
            ) : null}
            {post && postFollowers?.estimated ? (
                <AnalyticsGrid.Item
                    size={SIZE_FULL}
                    className="!min-h-0 rounded-m bg-icoGray-300 p-6 text-center font-GMedium text-xs text-icoGray-500 dark:bg-icoDarkMode-anthracite dark:text-icoDarkMode-beton">
                    <MediaViewerEstimatedStatsBanner network={socialNetworkType} />
                </AnalyticsGrid.Item>
            ) : null}
            {statsConfig &&
                statsConfig.length > 0 &&
                statsConfig.map((config) => {
                    const userNumbersFormat = user?.settings?.numbers_format ?? 'comma';

                    if (
                        config.type === 'kpi' &&
                        shouldRenderKpi(config, socialNetworkType, postFollowers?.value)
                    ) {
                        return (
                            <KpiGridItem
                                key={config.id}
                                config={config}
                                isLoading={isLoading}
                                isUserLoading={isUserLoading}
                                isLoadingPermission={isLoadingPermission}
                                isLoadingAnalytics={isLoadingAnalytics}
                                shouldUseAnalyticsV2={shouldUseAnalyticsV2}
                                analytics={analytics}
                                post={post ?? null}
                                getKpiValue={getKpiValue}
                                getKpiSplit={getKpiSplit}
                                socialNetworkType={socialNetworkType}
                                postFollowers={postFollowers}
                                permissions={permissions}
                                userNumbersFormat={userNumbersFormat}
                            />
                        );
                    }

                    if (config.type === 'chart') {
                        return (
                            <ChartGridItem
                                key={config.id}
                                config={config}
                                isLoading={isLoading}
                                isLoadingAnalytics={isLoadingAnalytics}
                                analytics={analytics}
                                userNumbersFormat={userNumbersFormat}
                            />
                        );
                    }

                    return null;
                })}
            {analyticsError ? (
                <AnalyticsGrid.Item size={SIZE_FULL} className="!min-h-0">
                    <InfoBanner
                        title={t('errors:error_occured')}
                        message={
                            <Trans
                                i18nKey="analytics:error_data_fetching"
                                components={{
                                    tag0: (
                                        <button
                                            key="error"
                                            className="text-icoRed underline"
                                            onClick={handleRetry}
                                        />
                                    )
                                }}
                            />
                        }
                    />
                </AnalyticsGrid.Item>
            ) : null}
        </AnalyticsGrid>
    );
};

export default MediaViewerStats;
